import { lerp, seg, smooth } from '../motion'
import { iso } from '../v3/iso'
import { BEATS, TOTAL_VH } from '../v5/beats'

/**
 * Zeitleiste der Version 7. Die Grundgeschichte aus Version 5 behält ihre
 * eigene Zeit `t` (0..1). Gewählte Extras werden als zusätzliche
 * Scrollstrecke hinter einer Station eingeschoben. Solange ein Extra läuft,
 * steht `t` still und das Extra bekommt seinen eigenen Fortschritt `p`.
 */

const END = Object.fromEntries(BEATS.map((b) => [b.id, b.at[1]]))

export function composeTimeline(extras) {
  const sorted = [...extras].sort((a, b) => END[a.after] - END[b.after])
  const extraVh = sorted.reduce((sum, ex) => sum + ex.len, 0)
  const total = TOTAL_VH + extraVh

  // Abschnitte in vh, abwechselnd Grundgeschichte und Extra
  const parts = []
  let t = 0
  let at = 0
  for (const ex of sorted) {
    const tEnd = END[ex.after]
    const len = (tEnd - t) * TOTAL_VH
    parts.push({ kind: 'base', g: [at, at + len], t: [t, tEnd] })
    at += len
    parts.push({ kind: 'extra', id: ex.id, g: [at, at + ex.len], t: tEnd })
    at += ex.len
    t = tEnd
  }
  parts.push({ kind: 'base', g: [at, total], t: [t, 1] })

  const toGlobal = (vh) => vh / total

  /** Globaler Fortschritt l → { t, extra, p } */
  const map = (l) => {
    const g = l * total
    const p = {}
    let state = null
    for (const part of parts) {
      const [a, b] = part.g
      if (part.kind === 'extra') {
        p[part.id] = seg(g, a, b)
        if (!state && g >= a && g < b)
          state = { t: part.t, extra: part.id, local: p[part.id] }
      } else if (!state && g >= a && g <= b) {
        state = {
          t: lerp(part.t[0], part.t[1], b > a ? (g - a) / (b - a) : 0),
          extra: null
        }
      }
    }
    return { ...(state || { t: 1, extra: null }), p }
  }

  // Kapitel für die Navigation, in globaler Reihenfolge
  const chapters = []
  for (const part of parts) {
    if (part.kind === 'extra') {
      const ex = sorted.find((e) => e.id === part.id)
      chapters.push({
        id: ex.id,
        label: ex.label,
        extra: true,
        at: [toGlobal(part.g[0]), toGlobal(part.g[1])]
      })
      continue
    }
    for (const b of BEATS) {
      const s = Math.max(b.at[0], part.t[0])
      const e = Math.min(b.at[1], part.t[1])
      if (e <= s) continue
      const len = part.t[1] - part.t[0]
      const g0 = part.g[0] + ((s - part.t[0]) / len) * (part.g[1] - part.g[0])
      const g1 = part.g[0] + ((e - part.t[0]) / len) * (part.g[1] - part.g[0])
      chapters.push({
        id: b.id,
        label: b.label,
        at: [toGlobal(g0), toGlobal(g1)]
      })
    }
  }

  return { total, map, chapters }
}

// ---------- Kamera der Extras ----------

/** Kameraposition aus Fokuspunkt in Welteinheiten. */
export const cam = (f, z, ox = 0) => {
  const p = iso(...f)
  return { x: p.x, y: p.y, z, ox }
}

/**
 * Interpoliert zwischen Einstellungen [{ p, cam }], wie in KombiStory mit
 * geglätteten Übergängen und logarithmischem Zoom.
 */
export function cameraAt(shots, p) {
  let i = 1
  while (i < shots.length - 1 && p > shots[i].p) i++
  const a = shots[i - 1]
  const b = shots[i]
  const k = smooth(seg(p, a.p, b.p))
  return {
    x: lerp(a.cam.x, b.cam.x, k),
    y: lerp(a.cam.y, b.cam.y, k),
    z: Math.exp(lerp(Math.log(a.cam.z), Math.log(b.cam.z), k)),
    ox: lerp(a.cam.ox, b.cam.ox, k)
  }
}
